import React, { useState, useRef, useEffect } from 'react'
import { ChatMessage } from './ChatMessage'
import { useChatStore } from '../store/chatStore'
import { useUserStore } from '../store/userStore'

export const ChatInterface = () => {
  const [inputValue, setInputValue] = useState('')
  const { messages, sendMessage, isLoading, error } = useChatStore()
  const { name } = useUserStore()
  const statusText = useChatStore((s) => s.statusText)
  const statusAgent = useChatStore((s) => s.statusAgent)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (inputValue.trim() && !isLoading) {
      sendMessage(inputValue)
      setInputValue('')
    }
  }

  return (
    <div className="rounded-xl overflow-hidden flex flex-col h-[calc(100vh-16rem)] sm:h-[calc(100vh-18rem)] md:h-[calc(100vh-20rem)] min-h-[400px] glass-effect shadow-lg border border-gray-100 chat-container">
      {/* Chat header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-2 sm:p-4 text-white flex items-center">
        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/20 p-1 mr-2 sm:mr-3 flex items-center justify-center">
          <img src="/images/ed-character-logo.png" alt="ED" className="w-5 h-5 sm:w-6 sm:h-6" />
        </div>
        <div>
          <h3 className="font-medium text-sm sm:text-base">ED</h3>
          <p className="text-xs text-white/80">Your intelligent assistant</p>
        </div>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-2 sm:p-4 space-y-2 sm:space-y-4 bg-gray-50/50">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-6">
            <img src="/images/ed-character-logo.png" alt="ED" className="w-16 h-16 mb-4 opacity-80 welcome-icon" />
            <h3 className="text-xl font-medium mb-2">Ask ED Anything</h3>
            <p className="text-muted-foreground max-w-md">
              {name ? `Hi ${name}! ` : ''}I can help with interior design, company projects, time tracking, policies, and more.
            </p>
          </div>
        ) : (
          <>
            {messages.map((message, index) => (
              <ChatMessage
                key={index}
                message={message}
                animate={message.role === 'assistant' && index === messages.length - 1}
              />
            ))}
            {isLoading && messages[messages.length - 1]?.role !== 'assistant' && (
              <div className="flex justify-start mb-2">
                <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold mr-1 sm:mr-2 flex-shrink-0 shadow-sm">
                  <span className="text-[10px] sm:text-xs">ED</span>
                </div>
                <div className="bg-white border border-gray-100 rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-sm max-w-[80%]">
                  {statusText ? (
                    <div className="flex items-center gap-2">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 flex items-center justify-center">
                        <span className="text-white text-[8px] font-bold">{statusAgent ? statusAgent.charAt(0) : '?'}</span>
                      </div>
                      <div>
                        {statusAgent && (
                          <div className="text-[10px] font-semibold text-indigo-600 uppercase tracking-wider">{statusAgent}</div>
                        )}
                        <div className="text-xs text-gray-600 animate-pulse">{statusText}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="text-xs text-gray-400">Thinking</div>
                      <div className="flex space-x-1">
                        <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-bounce" />
                        <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-bounce [animation-delay:0.15s]" />
                        <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-bounce [animation-delay:0.3s]" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {error && (
        <div className="bg-destructive/10 text-destructive p-3 text-sm">{error}</div>
      )}

      {/* Input area */}
      <form onSubmit={handleSubmit} className="border-t p-4 flex gap-2 bg-white">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="Type your message..."
          className="flex-1 p-3 border rounded-full bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/50"
          disabled={isLoading}
        />
        <button
          type="submit"
          className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-5 py-3 rounded-full disabled:opacity-50 transition-all duration-200 hover:shadow-md"
          disabled={isLoading || !inputValue.trim()}
        >
          {isLoading ? 'Sending...' : 'Send'}
        </button>
      </form>
    </div>
  )
}
